import { FileListItem } from "@/components/IBFile/interface";
import { ForexBuyState } from "@/feature/Forex/store/buy/interface";
import { $uploadForexFile } from "@/feature/Forex/api/form/forex-form";
import { ForexBuyDraft } from "@/feature/Forex/api/form/forex-form.interface";
import { Commit } from "vuex";
import { ForexBuyMutation } from "@/feature/Forex/store/buy/constants";
import { cachedOperDay } from "@/api/mainPage/operDay/operDay";
import ObjectUtils from "@/modules/Object/ObjectUtils";
import { forexFormRequestFields } from "@/feature/Forex/models/forex-form-request-fields";
import { ISendSumPaymentData } from "@/api/mainPage/payment-order/create-sum/interface";
import { profileEntity } from "@/models/modules/profile/entity";
import $t from '@/plugins/i18n/i18n';
import { ForexStatus } from "@/feature/Forex/api/history/forex-history.interface";

export const createForexBuyUploadFileFormData = (
	state: ForexBuyState,
	payload: FileListItem
): FormData => {
	const formData = new FormData();

	formData.append('file', payload.file!);
	formData.append('foreignExchangeId', String(state.currentDraft.foreignExchangeId));

	return formData;
};

export const createForexBuyUploadFileSource = (
	state: ForexBuyState,
	payload: FileListItem
): ReturnType<typeof $uploadForexFile> => {
	const uploadSource = $uploadForexFile(
		createForexBuyUploadFileFormData(state, payload), payload
	);

	state.uploadFileRequests[payload.fileId] = () => uploadSource.cancelTokenSource.cancel();

	return uploadSource;
};

export const leaveForexDraftKeys = (
	draft: ForexBuyDraft
): ForexBuyDraft => ObjectUtils.excludeEmpty(
	ObjectUtils.includeKeys(
		draft,
		forexFormRequestFields as (keyof ForexBuyDraft)[]
	)
);

export const getForexDraft = (
	state: ForexBuyState,
	payload?: ForexBuyDraft,
): ForexBuyDraft => leaveForexDraftKeys({
	...(payload || {}),
	operationType: payload?.operationType || state.operationType,
	operDay: cachedOperDay.currentDate,
	foreignExchangeId: state.draftIsSent
		? undefined
		: payload?.foreignExchangeId
});

export const getForexDraftWithId = (
	state: ForexBuyState,
	payload: ForexBuyDraft,
): ForexBuyDraft => {
	const draft = getForexDraft(state, payload);

	draft.foreignExchangeId = payload?.foreignExchangeId
		|| state.currentDraft.foreignExchangeId
		|| undefined;

	return draft;
};

export const sendAndSaveForexDraft = async (
	state: ForexBuyState,
	payload: ForexBuyDraft,
	commit: Commit,
	request: (draft?: ForexBuyDraft) => Promise<any>,
): Promise<ForexBuyDraft> => {
	const draft = await request(getForexDraftWithId(state, payload));

	draft && commit(ForexBuyMutation.setCurrentDraft, draft);

	return draft;
};

export const getForexCotractDetails = (
	draft?: ForexBuyDraft
): string => (
	!draft?.contractNumber
		? $t.t('forex.paymentOrderOnlyDetails')
		: $t.t('forex.paymentOrderDetails', [
			draft.contractNumber,
			(draft?.contractDate || '')
				.slice(0, 10)
				.split('-')
				.join('.')
		])
) as string || '';

export const getForexPaymentOrder = (
	draft?: ForexBuyDraft
): ISendSumPaymentData => ({
	operDay: draft?.operDay || '',
	paymentType: "PAYMENT",
	documentNumber: String(draft?.foreignExchangeId || '0'),
	srcName: profileEntity?.profileData?.ulName || '',
	srcAccountNumber: draft?.account || '',
	srcMfo: profileEntity?.profileData?.mfoUl || '',
	destAccountNumber: draft?.reservingAccount || '',
	destMfo: profileEntity?.profileData?.mfoUl || '',
	destinationName: profileEntity?.profileData?.ulName || '',
	knp: "00611",
	paymentAmount: draft?.requestAmount || 0,
	details: getForexCotractDetails(draft),
	status: ForexStatus.ON_DIRECTOR_LIST,
	foreignExchangeId: draft?.foreignExchangeId as number
});

export const deleteForexFileAttachment = (
	state: ForexBuyState,
	id: string,
) => {
	if (!state.currentDraft?.fileAttachments?.length) return;

	for (let i = 0; i < state.currentDraft.fileAttachments.length; i++) {
		if (state.currentDraft.fileAttachments[i]?.documentId === id) {
			state.currentDraft.fileAttachments.splice(i, 1);

			return;
		}
	}
};